import React, { useState, useEffect } from "react";
import { Button } from "~/components/v3/themed/Button";
import { Label } from "~/components/v3/shadcn/Label";
import Modal from "~/components/v3/themed/Modal";
import {
    getCommunitySponsorshipPackages,
    GetAllSponsorshipPackagesOutput,
    applyEventSponsorshipPackage,
} from "../../actions";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import RiverLoading from "~/components/themed/RiverLoading";
import { EventPreview } from "~/components/v3/events-components/EventPreview";
import { toastHandler } from "~/components/ToastHandler";

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    communityId: string;
    event: EventBySlugOutput;
    onSelectPackage: (packageId: string) => void;
};

const SelectSponsorshipPackageModal = ({
    isOpen,
    setIsOpen,
    communityId,
    event,
    onSelectPackage,
}: Props) => {
    const [packages, setPackages] = useState<GetAllSponsorshipPackagesOutput>(
        [],
    );
    const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const fetchedPackages =
                    await getCommunitySponsorshipPackages(communityId);
                setPackages(fetchedPackages);
                setIsLoading(false);
            } catch (error) {
                console.error("Failed to fetch sponsorship packages:", error);
                setIsLoading(false);
            }
        };
        if (isOpen) {
            fetchPackages();
        }
    }, [communityId, isOpen]);

    const handleSelectPackage = () => {
        if (selectedPackageId) {
            handleApplyPackage(event.id, selectedPackageId);
            onSelectPackage(selectedPackageId);
            setIsOpen(false);
        }
    };

    const handleApplyPackage = async (
        eventId: string,
        sponsorshipPackageId: string,
    ) => {
        toastHandler({
            loadingMessage: "Applying sponsorship package...",
            successMessage: "Sponsorship package applied successfully",
            errorMessage: "Failed to apply sponsorship package",
            operation: async () => {
                const updatedEvent = await applyEventSponsorshipPackage({
                    eventId,
                    sponsorshipPackageId,
                });
                return updatedEvent;
            },
        });
    };

    if (isLoading && isOpen) {
        return (
            <Modal
                title=""
                description=""
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <RiverLoading />
                <br />
                <br />
                <br />
                <br />
            </Modal>
        );
    }

    return (
        <Modal
            title="Select Sponsorship Package"
            description="Choose a sponsorship package to apply to this event"
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <div className="space-y-4">
                <EventPreview event={event} />
                <div className="space-y-4">
                    <Label>Available Packages</Label>
                    <div className="space-y-4">
                        {packages.map((pkg) => (
                            <div
                                key={pkg.id}
                                className={`
                                    cursor-pointer space-y-2 rounded-md p-4
                                    transition-all duration-200 ease-in-out
                                    ${selectedPackageId === pkg.id ? "border-2 border-v3-primary" : "border border-gray-300"}
                                    hover:scale-[1.02] hover:border-2 hover:border-v3-primary
                                `}
                                onClick={() => setSelectedPackageId(pkg.id)}
                            >
                                <div className="flex items-center space-x-2">
                                    <Label className="font-bold">
                                        {pkg.name}
                                    </Label>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">
                                        {pkg.description}
                                    </p>
                                    <div className="mt-2">
                                        <Label className="text-sm font-semibold">
                                            Tiers:
                                        </Label>
                                        <ul className="list-inside list-disc">
                                            {pkg.packageTiers.map((tier) => (
                                                <li
                                                    key={tier.id}
                                                    className="text-sm"
                                                >
                                                    {tier.name} - $
                                                    {tier.priceDollars}.
                                                    {tier?.priceCents ??
                                                        (0)
                                                            .toString()
                                                            .padStart(
                                                                2,
                                                                "0",
                                                            )}{" "}
                                                    {tier.currencyCode}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <Button
                    onClick={handleSelectPackage}
                    disabled={!selectedPackageId}
                >
                    Apply Selected Package
                </Button>
            </div>
        </Modal>
    );
};

export default SelectSponsorshipPackageModal;
